import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import '../styles/Sidebar.css';

/**
 * SuperAdminSidebar - Dedicated navigation component for super admin role
 * 
 * Security Note: This component is exclusively for super_admin role.
 * It provides access only to platform-wide management features:
 * - Dashboard (system overview)
 * - Centers (center management)
 * - Users (cross-center user management)
 * - Settings (account settings)
 * 
 * Explicitly excluded:
 * - Messages (center-specific communication)
 * - Admin tools (attendance, classes, reports)
 * - Center-specific functionality
 */
const SuperAdminSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add resize listener (must be called before any conditional returns)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Security check: Only render for super_admin role (after hooks)
  if (!user || user.role !== 'super_admin') {
    console.warn('SuperAdminSidebar: Unauthorized access attempt by role:', user?.role);
    return null;
  }

  const handleNavLinkClick = (path) => {
    // Close the sidebar on mobile
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Get active class
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Super Admin specific routes only
  const superAdminRoutes = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/centers', label: 'Centers', icon: '🏢' },
    { path: '/announcements', label: 'Announcements', icon: '📢' },
    { path: '/settings', label: 'Settings', icon: '⚙️' }
  ];

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="sidebar-overlay visible" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Close button (mobile only) */}
        <button
          className="close-sidebar"
          onClick={onClose}
        >
          ✖
        </button>
        
        {/* Sidebar header */}
        <div className="sidebar-header">
          <div className="sidebar-title">
            <span className="role-badge super-admin">Super Admin</span>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="nav-menu">
          {superAdminRoutes.map((route) => (
            <Link
              key={route.path}
              to={route.path}
              className={`nav-item ${isActive(route.path) ? 'active' : ''}`}
              onClick={() => handleNavLinkClick(route.path)}
            >
              <span className="nav-icon">{route.icon}</span>
              {route.label}
            </Link>
          ))}
        </nav>
        
        {/* User Info */}
        <div className="user-info">
          <div className="user-avatar">🔧</div>
          <div className="user-details">
            <p className="user-name">{user?.name || 'Super Admin'}</p>
            <p className="user-role">Super Administrator</p>
          </div>
          <button
            className="logout-btn"
            onClick={() => {
              if (window.confirm('Are you sure you want to log out?')) {
                logout();
              }
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default SuperAdminSidebar;